// grid open [<name>]  — M8a.3
//
// Opens the entity's outlet URL in the default browser.
//
// Resolution (spec v17 §13.3):
//   - No <name> arg, in a linked dir:  uses link.json entity_id + validates
//     active-org mismatch (§5).
//   - <name> arg given: passes the name directly as the :id path segment;
//     the server resolves UUID or display_name.
//
// Behavior:
//   - Fetches entity.url from GET /api/v2/runtimes/:id
//   - With --print: writes the URL to stdout and exits 0 (no browser). Headless
//     / CI use — capture the URL without launching anything.
//   - If entity.status === 'charged': prints notice and exits 0 (no browser)
//   - Otherwise: prints "Opening <url>" and spawns the platform browser opener

import { Command } from 'commander';
import { spawn } from 'child_process';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { requireLink, validateLinkVsActiveOrg } from '../services/link.js';
import { tryGetActiveOrg } from '../active-org.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import type { V2RuntimeDetailResponse } from './status.js';

// ── Entity resolution ─────────────────────────────────────────────────────

/**
 * Returns the entity identifier to use as the :id path segment.
 *
 * No name arg → reads link.json, validates org mismatch if active org is known,
 * and returns the link's entity_id (a server-issued UUID).
 *
 * Name arg → returns as-is; the server resolves UUID or display_name.
 */
async function resolveEntityId(
  client: IV2Client,
  nameArg: string | undefined,
): Promise<string> {
  if (nameArg !== undefined) return nameArg;
  const link = requireLink();
  const activeOrg = await tryGetActiveOrg(client);
  if (activeOrg !== null) {
    validateLinkVsActiveOrg(link, activeOrg);
  }
  return link.entity_id;
}

// ── Browser opener ────────────────────────────────────────────────────────

/**
 * Spawns the platform browser opener with the URL as a separate argv element
 * — never as part of a shell-interpreted command line. This avoids both:
 *   1. Quote-escape bugs (`$`, backtick, backslash, embedded quotes in URLs).
 *   2. The Windows `start` quirk where `start "url"` treats the URL as the
 *      window TITLE and opens an empty cmd window. The correct invocation is
 *      `cmd /c start "" "<url>"` — empty quoted title, then the URL.
 *
 * The child is spawned detached and immediately `unref`'d so the CLI process
 * returns without blocking on the browser opener exiting.
 */
function openBrowser(url: string): void {
  let command: string;
  let args: string[];
  switch (process.platform) {
    case 'darwin':
      command = 'open';
      args = [url];
      break;
    case 'win32':
      // Empty "" is the window title; the URL is a separate argv element.
      command = 'cmd';
      args = ['/c', 'start', '', url];
      break;
    default:
      command = 'xdg-open';
      args = [url];
      break;
  }
  const child = spawn(command, args, { stdio: 'ignore', detached: true });
  child.unref();
}

// ── Command implementation ────────────────────────────────────────────────

export interface OpenOpts {
  /** Print the URL instead of opening a browser (headless / CI use). */
  print?: boolean;
  json?: boolean;
  output?: string;
}

/**
 * Core implementation. Accepts an optional `client` override for unit tests.
 * In production, the client is created via `getV2Client()`.
 */
export async function runOpen(
  nameArg: string | undefined,
  opts: OpenOpts = {},
  client?: IV2Client,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const apiClient = client ?? (await getV2Client());
  const resolvedId = await resolveEntityId(apiClient, nameArg);
  const entity = await apiClient.get<V2RuntimeDetailResponse>(
    `/api/v2/runtimes/${encodeURIComponent(resolvedId)}`,
  );

  // Structured mode reports the URL + status and never launches a browser —
  // scripts capture the outlet; a subprocess would be meaningless to them.
  if (out.isStructured) {
    out.json(() => ({ url: entity.url, status: entity.status }));
    return;
  }

  if (opts.print) {
    process.stdout.write(`${entity.url}\n`);
    return;
  }

  if (entity.status === 'charged') {
    process.stdout.write('Entity is not yet live. Run: grid plug\n');
    return;
  }

  process.stdout.write(`Opening ${entity.url}\n`);
  openBrowser(entity.url);
}

// ── Commander command export ──────────────────────────────────────────────

export const openCommand = addOutputOption(
  new Command('open')
    .description('Open the entity outlet URL in the default browser')
    .argument('[name]', 'Entity name or UUID (uses linked entity if omitted)')
    .option('--print', 'Print the outlet URL instead of opening a browser')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: OpenOpts) => {
  try {
    await runOpen(name, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
